import { Redirect, Route, Switch } from 'react-router-dom';
import { ClinicianRoutePrefix, paths, Routes } from 'routes';

import { PrivateRoute } from 'navigation/router/components/routes/private-route';

import { Role } from 'features/auth/types';

import { Alert, Skeleton, Spin } from 'antd';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { useReduxAuthStore } from 'lib/hooks/auth/use-auth';
import {
  ClinicianContext,
  useCreateClinicianContextState
} from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useErrorList } from 'lib/utils/useErrorList';
import { useEffect } from 'react';
import { CLINICIAN_ROUTES } from '../config/routes-clinician';
import { ClinicContextForm } from './form-clinic-context';

export function ClinicianRouter() {
  const currentUser = useReduxAuthStore();

  /**
   * Do not render the page while the user data is loading
   * This should not happen because the main router will only grant access to this route if the users role matches
   */
  if (!currentUser.user?.uid) {
    return <Skeleton />;
  }

  return <ClinicianRouteWithContext clinicianId={currentUser.user.uid} />;
}

export function ClinicianRouteWithContext(props: { clinicianId: string }) {
  const clinicianInfoState = useCreateClinicianContextState(props.clinicianId);
  const errorList = useErrorList();

  /**
   * Add critical errors to the error list when they are available
   */
  useEffect(
    () => {
      if (clinicianInfoState.hasErrors) {
        errorList.reset();
        clinicianInfoState.errors.forEach(_error => {
          const error = new Error(`[ClinicInfo]: ${_error.message}`);

          return errorList.addError(error);
        });
      }
    },
    // eslint-disable-next-line
    [clinicianInfoState.errors]
  );

  /**
   * Show a critical error when the Clinic data could not be loaded
   */
  if (clinicianInfoState.hasErrors) {
    return <ErrorListDisplay errorList={errorList} />;
  }

  /**
   * Show a loading animation while the Clinic data is loading
   */
  if (!clinicianInfoState.isFetched) {
    return (
      <div>
        <Text>
          Loading clinic data... <Spin />
        </Text>
        <Skeleton active />
      </div>
    );
  }

  if (clinicianInfoState.clinicNames.length === 0) {
    return (
      <div>
        <Alert
          type="warning"
          message={<Text>You are not assigned to any Clinics</Text>}
        />
      </div>
    );
  }

  /**
   * Render the Clinician routes
   *
   * WARNING: These routes are conditionally added to the router context
   */
  return (
    <ClinicianContext value={clinicianInfoState}>
      <ClinicContextForm />
      {clinicianInfoState.currentClinic ? (
        <Switch>
          <Route exact path={ClinicianRoutePrefix}>
            <Redirect to={Routes.ClinicianHome} />
          </Route>

          {CLINICIAN_ROUTES.map(config => (
            <PrivateRoute
              key={`clinicianroutesmap-${config.path}`}
              roles={config.roles ?? [Role.Clinician, Role.ClinicAdmin]}
              path={config.path}
              component={config.component}
              redirectPath={config.redirectPath ?? paths.signIn()}
              exact={config.exact}
            />
          ))}
        </Switch>
      ) : (
        <div
          style={{
            marginTop: '2rem'
          }}
        >
          <Alert message="Please select a Clinic" />
        </div>
      )}
    </ClinicianContext>
  );
}
